import { ApplicationState } from '@/store';
import { getRole } from '@/store/selectors';
import { hasPerm, P } from '@/utilities/roles/permissions';
import { Box, Button, Flex, Menu, MenuButton, MenuItem, MenuList, useDisclosure } from '@chakra-ui/react';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { parseDate, formatDate, Icon } from 'shared-components';
import { JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BorderedRow } from '../../../atoms/bordered-row';
import type { MultiFileUploadV2FileItem as FileItem } from 'shared-components';
import {
  AgentApplicantAuthorisationStatus,
  ApplicantOfferPaymentMethod,
  ApplicantOfferPaymentSponsorType,
  ApplicantOfferPaymentStatus,
  IApplicantOfferPayment,
  DocumentKey,
  DocumentType,
} from 'types';
import {
  updateApplicantOfferPaymentDepositStatus,
  updateApplicantOfferPaymentProofStatus,
  deleteApplicantOfferProofOfPaymentFile,
} from '../../../../store/apply/actions';
import { FormattedText, PreviewLink } from './FormattedFulfilmentData';
import { Note, NoteType } from './Note';
import { EditTextFieldModal } from './modals/EditTextFieldModal';
import UploadIconBtn from './UploadIconBtn';
import { useDocumentSave } from '@/hooks/useDocumentSave';
import { DocumentViewer } from '../../../organisms/DocumentViewer';

type PaymentProps = {
  payment: IApplicantOfferPayment;
  userId: string;
  uni: string;
  offerId: string;
};

const readableStatuses = {
  [ApplicantOfferPaymentStatus.Request]: 'Request',
  [ApplicantOfferPaymentStatus.Review]: 'Review',
  [ApplicantOfferPaymentStatus.Exempt]: 'Exempt',
  [ApplicantOfferPaymentStatus.Approved]: 'Satisfied',
};

const statusSelectStyle = {
  [ApplicantOfferPaymentStatus.Request]: {
    colorScheme: 'blue',
  },
  [ApplicantOfferPaymentStatus.Review]: {
    colorScheme: 'blue',
    variant: 'outline',
  },
  [ApplicantOfferPaymentStatus.Approved]: {
    colorScheme: 'green',
  },
  [ApplicantOfferPaymentStatus.Exempt]: {
    colorScheme: 'gray',
  },
};

/**
 * Component to display the payment deposit details for an applicant's offer.
 * Editing disabled for some users.
 */
export function PaymentDeposit({ payment, userId, uni, offerId }: Readonly<PaymentProps>): JSX.Element {
  const originalStatus = payment.status ?? ApplicantOfferPaymentStatus.Request;
  const [status, setStatus] = useState(originalStatus);
  const { applyConfig } = useSelector((state: ApplicationState) => state.applyConfig);
  const dispatch = useDispatch();
  const { isOpen: isNoteModalOpen, onOpen: openNoteModal, onClose: closeNoteModal } = useDisclosure();
  // Tracks whether the modal was opened as part of a status change (Exempt/Approved), as opposed to
  // the user clicking the edit icon on an existing note. When true, cancelling the modal rolls back
  // the optimistic status update. Otherwise closing the modal should have no effect on status.
  const [openedForStatusChange, setOpenedForStatusChange] = useState(false);

  const role = useSelector(getRole);

  const { agentAccessStatus } = useSelector((state: ApplicationState) => state.apply);
  const isAgentAccessDeniedByAppliant = agentAccessStatus === AgentApplicantAuthorisationStatus.AUTHORISATION_DENIED;

  const canEditOffer = hasPerm(role, P.EDIT_APPLY_OFFER) && !isAgentAccessDeniedByAppliant;

  useEffect(() => {
    setStatus(originalStatus);
  }, [payment]);

  const handleStatusUpdate = useCallback(
    (statusToUpdate: ApplicantOfferPaymentStatus, newNoteText?: string) => {
      dispatch(
        updateApplicantOfferPaymentDepositStatus({
          applicantId: userId,
          uni,
          offerId,
          status: statusToUpdate,
          statusInternalNote: newNoteText,
        }),
      );
    },
    [offerId, dispatch, uni, userId],
  );

  const handleStatusChange = (newStatus: ApplicantOfferPaymentStatus): void => {
    setStatus(newStatus);
    if ([ApplicantOfferPaymentStatus.Exempt, ApplicantOfferPaymentStatus.Approved].includes(newStatus)) {
      setOpenedForStatusChange(true);
      openNoteModal();
    } else {
      handleStatusUpdate(newStatus);
    }
  };

  const { text, subtext } = useMemo(() => {
    let calculatedText;
    let calculatedSubtext;

    if (payment.paymentMethod === ApplicantOfferPaymentMethod.Deposit) {
      calculatedText = `£${payment?.depositDetail?.depositAmount?.toLocaleString() ?? '0'} deposit paid`;
      calculatedSubtext = formatDate(parseDate(payment.depositDetail?.paymentDate), 'D MMM YYYY');
    } else if (payment.paymentMethod === ApplicantOfferPaymentMethod.Sponsorship) {
      const { sponsorshipDetail } = payment;
      calculatedText = `Sponsorship from ${sponsorshipDetail?.sponsorName}`;
      if (
        [
          ApplicantOfferPaymentSponsorType.InternationalCompany,
          ApplicantOfferPaymentSponsorType.InternationalOrganisation,
        ].includes(sponsorshipDetail.sponsorType)
      ) {
        const isInternationalCompany =
          sponsorshipDetail.sponsorType === ApplicantOfferPaymentSponsorType.InternationalCompany;
        calculatedSubtext = `International ${isInternationalCompany ? 'company' : 'organisation'} \n ${
          sponsorshipDetail?.isSponsorOrgMultinational ? 'Presence' : 'No presence'
        } in more than in  1 country`;
      } else {
        calculatedSubtext = sponsorTypeLabelMap[sponsorshipDetail.sponsorType];
      }
    } else if (payment.paymentMethod === ApplicantOfferPaymentMethod.SLCFunding) {
      calculatedText = 'Student Loan Company financing';
      calculatedSubtext = `CRN: ${payment.slcDetail?.customerRefNumber} \n SSN: ${payment.slcDetail?.studentSupportNumber}`;
    }

    return { text: calculatedText, subtext: calculatedSubtext };
  }, [payment]);

  return (
    <BorderedRow>
      <Flex alignItems="start" gap={2}>
        <Box flexGrow={1} wordBreak="break-word" fontSize={'lg'} lineHeight={6}>
          <Box>
            {!text && 'Provide '}
            {applyConfig.depositText}
          </Box>
          {text && <FormattedText text={text} subtext={subtext} />}
        </Box>

        <Flex alignItems="center" gap={2} flexShrink={0} my={-1}>
          <PaymentStatusMenu status={status} disableEdit={!canEditOffer} onStatusChange={handleStatusChange} />
          <Box w="32px" flexShrink={0} />
        </Flex>
      </Flex>

      <PaymentNote
        status={status}
        noteText={payment.statusInternalNote || ''}
        onEdit={() => {
          setOpenedForStatusChange(false);
          openNoteModal();
        }}
        disableEdit={!canEditOffer}
      />

      <EditTextFieldModal
        isOpen={isNoteModalOpen}
        onClose={() => {
          closeNoteModal();
          if (openedForStatusChange) {
            setStatus(originalStatus);
          }
          setOpenedForStatusChange(false);
        }}
        onSave={(note: string) => {
          handleStatusUpdate(status, note);
          closeNoteModal();
        }}
        title={payment.statusInternalNote ? 'Edit internal note' : 'Add internal note'}
        fieldLabel={payment.statusInternalNote ? 'Internal note' : 'Internal note (optional)'}
        fieldSubLabel="Not shared with the applicant"
        placeholder="Add note..."
        initialValue={payment.statusInternalNote ?? ''}
      />
    </BorderedRow>
  );
}

/**
 * Component to display the proof of payment details for an applicant's offer.
 * Editing disabled for some users.
 */
export function PaymentProof({ payment, userId, uni, offerId }: Readonly<PaymentProps>): JSX.Element {
  const { uploadedSuccessfully, fileUploadError, deletedSuccessfully } = useSelector(
    (state: ApplicationState) => state.student.documents,
  );
  const originalStatus = payment.proofOfPayment?.status ?? ApplicantOfferPaymentStatus.Request;
  const [status, setStatus] = useState(originalStatus);
  const dispatch = useDispatch();
  const { isOpen: isNoteModalOpen, onOpen: openNoteModal, onClose: closeNoteModal } = useDisclosure();
  // Tracks whether the modal was opened as part of a status change (Exempt/Approved), as opposed to
  // the user clicking the edit icon on an existing note. When true, cancelling the modal rolls back
  // the optimistic status update. Otherwise closing the modal should have no effect on status.
  const [openedForStatusChange, setOpenedForStatusChange] = useState(false);

  const role = useSelector(getRole);
  const canEditOffer = hasPerm(role, P.EDIT_APPLY_OFFER);
  const { handleSaveDocument } = useDocumentSave();

  useEffect(() => {
    setStatus(originalStatus);
  }, [payment]);

  const handleStatusUpdate = useCallback(
    (statusToUpdate: ApplicantOfferPaymentStatus, newNoteText?: string) => {
      dispatch(
        updateApplicantOfferPaymentProofStatus({
          applicantId: userId,
          uni,
          offerId,
          status: statusToUpdate,
          statusInternalNote: newNoteText,
        }),
      );
    },
    [offerId, dispatch, uni, userId],
  );

  const handleStatusChange = (newStatus: ApplicantOfferPaymentStatus): void => {
    setStatus(newStatus);
    if ([ApplicantOfferPaymentStatus.Exempt, ApplicantOfferPaymentStatus.Approved].includes(newStatus)) {
      setOpenedForStatusChange(true);
      openNoteModal();
    } else {
      handleStatusUpdate(newStatus);
    }
  };

  const handleUpload = (files: FileItem[], deletedS3Keys?: string[]): void => {
    const newFileNames: string[] = [];
    const newS3Keys: string[] = [];

    for (const file of files) {
      const fileWithMeta = file as FileItem & { url?: string; s3Key?: string };
      if (fileWithMeta.url) {
        newFileNames.push(file.name);
        const URL_PATH_SKIP_COUNT = 3; // Skip protocol, empty string, and domain
        const derivedKey =
          fileWithMeta.s3Key || fileWithMeta.url.split('/').slice(URL_PATH_SKIP_COUNT).join('/').split('?')[0];
        if (derivedKey) newS3Keys.push(derivedKey);
      }
    }

    const toDelete = deletedS3Keys ?? [];
    const existingS3Keys = (payment?.proofOfPayment?.s3Keys || []).filter(k => !toDelete.includes(k));
    const existingFileNames = existingS3Keys.map(k => k.split('/').pop() || k);

    const fileNames = [...existingFileNames, ...newFileNames];
    const s3Keys = [...existingS3Keys, ...newS3Keys];

    if (fileNames.length > 0) {
      const docType = DocumentType.ProofOfPayment;
      const docKey = DocumentKey[docType];

      handleSaveDocument({
        id: 'proof-of-payment',
        docType,
        docKey,
        fileNames,
        s3Keys,
        uploadedBy: userId,
      });
    }
  };

  const handleDeleteExistingFile = useCallback(
    async (s3KeyToDelete: string) => {
      // Find the file name for the s3Key
      const currentProofOfPayment = payment?.proofOfPayment;
      if (!currentProofOfPayment?.s3Keys) {
        return;
      }

      const FILE_NOT_FOUND_INDEX = -1;
      const fileIndex = currentProofOfPayment.s3Keys.findIndex(key => key === s3KeyToDelete);
      if (fileIndex === FILE_NOT_FOUND_INDEX) {
        return;
      }

      // Extract the file name from the s3Key or use the fileName array
      let fileName = s3KeyToDelete.split('/').pop() || s3KeyToDelete;
      if (currentProofOfPayment.fileName) {
        const fileNames = currentProofOfPayment.fileName.split(',');
        fileName = fileNames[fileIndex] || fileName;
      }

      // Dispatch the saga action
      dispatch(
        deleteApplicantOfferProofOfPaymentFile({
          uni,
          applicantId: userId,
          offerId,
          s3Key: s3KeyToDelete,
          fileName,
        }),
      );
    },
    [payment, uni, userId, offerId, dispatch],
  );

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewUrls, setPreviewUrls] = useState<string[]>();
  const openFile = (urls: string[]): void => {
    setPreviewUrls(urls);
    setIsPreviewOpen(true);
  };

  const fileName = useMemo(() => {
    if (payment?.paymentMethod === ApplicantOfferPaymentMethod.Deposit) {
      return 'Proof of deposit';
    } else if (payment?.paymentMethod === ApplicantOfferPaymentMethod.Sponsorship) {
      return 'Sponsorship letter';
    }
    return 'Proof of financing';
  }, [payment]);

  return (
    <BorderedRow>
      <Flex alignItems="start" gap={2} fontSize={'lg'} lineHeight={6}>
        <Box flexGrow={1} wordBreak="break-word">
          <Box>
            {!payment?.proofOfPayment?.urls && 'Provide '}
            Proof of payment
          </Box>
          {payment?.proofOfPayment?.urls && (
            <PreviewLink
              preview={openFile}
              urls={payment.proofOfPayment.urls}
              text={fileName}
              count={payment.proofOfPayment.urls.length}
            />
          )}
        </Box>

        <Flex alignItems="center" gap={2} flexShrink={0} my={-1}>
          <PaymentStatusMenu status={status} disableEdit={!canEditOffer} onStatusChange={handleStatusChange} />
          <UploadIconBtn
            onProceed={handleUpload}
            onDeleteExistingFile={handleDeleteExistingFile}
            uploadedSuccessfully={uploadedSuccessfully}
            fileUploadError={fileUploadError}
            deletedSuccessfully={deletedSuccessfully}
            documentConfig={{
              docType: DocumentType.ProofOfPayment,
              docKey: DocumentKey[DocumentType.ProofOfPayment],
              label: 'Proof of payment',
            }}
            existingFiles={{
              urls: payment?.proofOfPayment?.urls,
              s3Keys: payment?.proofOfPayment?.s3Keys,
            }}
          />
        </Flex>
      </Flex>

      <PaymentNote
        status={status}
        noteText={payment.proofOfPayment?.statusInternalNote || ''}
        onEdit={() => {
          setOpenedForStatusChange(false);
          openNoteModal();
        }}
        disableEdit={!canEditOffer}
      />

      <EditTextFieldModal
        isOpen={isNoteModalOpen}
        onClose={() => {
          closeNoteModal();
          if (openedForStatusChange) {
            setStatus(originalStatus);
          }
          setOpenedForStatusChange(false);
        }}
        onSave={(note: string) => {
          handleStatusUpdate(status, note);
          closeNoteModal();
        }}
        title={payment.proofOfPayment?.statusInternalNote ? 'Edit internal note' : 'Add internal note'}
        fieldLabel={payment.proofOfPayment?.statusInternalNote ? 'Internal note' : 'Internal note (optional)'}
        fieldSubLabel="Not shared with the applicant"
        placeholder="Add note..."
        initialValue={payment.proofOfPayment?.statusInternalNote ?? ''}
      />

      {isPreviewOpen && <DocumentViewer documents={previewUrls} onClose={() => setIsPreviewOpen(false)} />}
    </BorderedRow>
  );
}

type PaymentNoteProps = {
  status: ApplicantOfferPaymentStatus;
  noteText: string;
  onEdit: () => void;
  disableEdit?: boolean;
};

function PaymentNote({
  status,
  noteText,
  onEdit,
  disableEdit = false,
}: Readonly<PaymentNoteProps>): JSX.Element | null {
  if (![ApplicantOfferPaymentStatus.Exempt, ApplicantOfferPaymentStatus.Approved].includes(status)) {
    return null;
  }

  // Only hide when there is no note AND editing is disabled.
  // When editable with no note, keep the row visible so the user can click the edit icon to add one.
  if (!noteText && disableEdit) {
    return null;
  }

  return (
    <Note
      noteText={noteText}
      onEdit={onEdit}
      id={NoteType.InternalNote}
      status={NoteType.InternalNote}
      index={0}
      disableEdit={disableEdit}
    />
  );
}

interface PaymentStatusMenuProps {
  status: ApplicantOfferPaymentStatus;
  onStatusChange: (status: ApplicantOfferPaymentStatus) => void;
  disableEdit?: boolean;
}

function PaymentStatusMenu({
  status,
  onStatusChange,
  disableEdit = false,
}: Readonly<PaymentStatusMenuProps>): JSX.Element {
  const statusStyleProps = statusSelectStyle[status];

  return (
    <>
      <Menu placement="bottom-end">
        <MenuButton
          as={Button}
          isDisabled={disableEdit}
          rightIcon={<Icon icon={faAngleDown} />}
          variant="status"
          size="sm"
          {...statusStyleProps}
        >
          {readableStatuses[status]}
        </MenuButton>
        <MenuList>
          <MenuItem onClick={() => onStatusChange(ApplicantOfferPaymentStatus.Request)}>Request</MenuItem>
          <MenuItem onClick={() => onStatusChange(ApplicantOfferPaymentStatus.Review)}>Review</MenuItem>
          <MenuItem onClick={() => onStatusChange(ApplicantOfferPaymentStatus.Exempt)}>Exempt</MenuItem>
          <MenuItem onClick={() => onStatusChange(ApplicantOfferPaymentStatus.Approved)}>Satisfied</MenuItem>
        </MenuList>
      </Menu>
    </>
  );
}

const sponsorTypeLabelMap: Record<ApplicantOfferPaymentSponsorType, string> = {
  [ApplicantOfferPaymentSponsorType.UkGovernment]: 'UK Government',
  [ApplicantOfferPaymentSponsorType.BritishCouncil]: 'British Council',
  [ApplicantOfferPaymentSponsorType.YourGovernment]: 'Your Government',
  [ApplicantOfferPaymentSponsorType.InternationalOrganisation]: 'International Organisation',
  [ApplicantOfferPaymentSponsorType.InternationalCompany]: 'International Company',
};
